import { useId } from 'react'
import { cn } from '@/lib/utils'
import { getThemeColor } from '@/shared/hooks/useThemeColors'
import { useTheme } from '@/shared/hooks/useTheme'

interface DbShellProps {
  className?: string
}

/**
 * Minimalist 3-level stacked-disk database shell rendered as fine-line SVG.
 * 4 ellipses define 3 distinct storage segments.
 *
 * ViewBox: 120×140 — vertical center at y=70.
 * Disks: y=34, 58, 82, 106 — 24px gaps, symmetric around y=70.
 * Structure center (34+106)/2 = 70 = viewBox center. Pixel-perfect.
 *
 *   Disk 1 (top)       y=34   — 0.8px stroke
 *   ── Segment 1 ──
 *   Disk 2 (mid-upper) y=58   — 0.6px stroke, 60% opacity
 *   ── Segment 2 (eye zone, centered at y=70) ──
 *   Disk 3 (mid-lower) y=82   — 0.6px stroke, 60% opacity
 *   ── Segment 3 ──
 *   Disk 4 (bottom)    y=106  — 0.8px stroke
 */
export function DbShell({ className }: DbShellProps) {
  const filterId = useId()

  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const strokeColor = getThemeColor('--chart-line')
  const glowColor = getThemeColor('--chart-glow')

  const cx = 60
  const rx = 48
  const ry = 10

  // Symmetric around viewBox center y=70, 24px gaps
  const d1 = 34 // Disk 1 — top
  const d2 = 58 // Disk 2 — mid-upper
  const d3 = 82 // Disk 3 — mid-lower
  const d4 = 106 // Disk 4 — bottom

  return (
    <svg
      viewBox="0 0 120 140"
      fill="none"
      className={cn('pointer-events-none', className)}
      aria-hidden="true"
    >
      <defs>
        <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="4"
            floodColor={glowColor}
            floodOpacity="1"
          />
        </filter>
      </defs>

      <g filter={`url(#${filterId})`} opacity={isDark ? 0.5 : 0.4}>
        {/* Disk 1 — top */}
        <ellipse cx={cx} cy={d1} rx={rx} ry={ry} stroke={strokeColor} strokeWidth="0.8" />

        {/* Disk 2 — mid-upper */}
        <ellipse
          cx={cx}
          cy={d2}
          rx={rx}
          ry={ry}
          stroke={strokeColor}
          strokeWidth="0.6"
          strokeOpacity="0.6"
        />

        {/* Disk 3 — mid-lower */}
        <ellipse
          cx={cx}
          cy={d3}
          rx={rx}
          ry={ry}
          stroke={strokeColor}
          strokeWidth="0.6"
          strokeOpacity="0.6"
        />

        {/* Disk 4 — bottom */}
        <ellipse cx={cx} cy={d4} rx={rx} ry={ry} stroke={strokeColor} strokeWidth="0.8" />

        {/* Left edge line: top → bottom */}
        <line
          x1={cx - rx}
          y1={d1}
          x2={cx - rx}
          y2={d4}
          stroke={strokeColor}
          strokeWidth="0.5"
        />
        {/* Right edge line: top → bottom */}
        <line
          x1={cx + rx}
          y1={d1}
          x2={cx + rx}
          y2={d4}
          stroke={strokeColor}
          strokeWidth="0.5"
        />

        {/* Bottom closing arc (lower half of cylinder base) */}
        <path
          d={`M ${cx - rx},${d4} A ${rx},${ry} 0 0,0 ${cx + rx},${d4}`}
          stroke={strokeColor}
          strokeWidth="0.5"
          strokeOpacity="0.3"
        />

        {/* Subtle inner horizontal grid lines — one per segment midpoint */}
        {[
          (d1 + d2) / 2, // Segment 1: y=46
          (d2 + d3) / 2, // Segment 2: y=70 (viewBox center — eye sits here)
          (d3 + d4) / 2, // Segment 3: y=94
        ].map((y, i) => (
          <line
            key={i}
            x1={cx - rx + 8}
            y1={y}
            x2={cx + rx - 8}
            y2={y}
            stroke={strokeColor}
            strokeWidth="0.3"
            strokeOpacity="0.15"
          />
        ))}
      </g>
    </svg>
  )
}
